import React from "react";
import { _cs } from "@togglecorp/fujs";
import { Translation } from "react-i18next";
import { connect } from "react-redux";
import { RouterProps, WithRouter } from "src/hooks/WithRouter";

import ListView from "#rscv/List/ListView";
import Icon from "#rscg/Icon";
import modalize from "#rscg/Modalize";
import { authStateSelector, languageSelector } from "#selectors";
import {
  setAuthAction,
  setInitialCloseWalkThroughAction,
} from "#actionCreators";
import { getAuthState } from "#utils/session";
import {
  createConnectedRequestCoordinator,
  createRequestClient,
  NewProps,
  ClientAttributes,
  methods,
} from "#request";
import CitizenReportFormModal from "#components/CitizenReportFormModal";
import CitizenReportsModal from "#components/CitizenReportsModal";
import NewLoginModal from "#components/NewLoginModal";
import AboutModal from "#components/AboutModal";
import SituationReport from "#components/SituationReportModal";
import Relief from "#components/ReliefModal";
import FeedbackSupport from "#views/FeedbackSupport";
import ScalableVectorGraphics from "#rscv/ScalableVectorGraphics";
import ButtonGroupLogo from "#resources/icons/sidebarGroupButtons.svg?react";
import PageContext from "#components/PageContext";
import ReportIncidentIcon from "#resources/icons/reportIncident.svg?react";

import MenuItem, { MenuItemProps } from "./MenuItem";
import GroupMenuContainer from "./GroupMenuContainer";

import {
  NAVBAR_ROUTE_PRELOAD_ORDER,
  ROUTES,
  preloadRouteByName,
} from "#routes/routes";
import type { AppDispatch } from "#store";
import type { AppState } from "#store/types";
import type { AuthState } from "#store/atom/auth/types";
import type { AppRoute } from "#routes/types";
import styles from "./styles.module.scss";

type NavbarRoute = AppRoute & {
  path: string;
  meta: {
    navbar: true;
    title: string;
    titleNep?: string;
    iconName?: string;
    disabled?: boolean;
    disableIfNoAuth?: boolean;
    id: string;
  };
};
const pagesNew = ROUTES.filter((r): r is NavbarRoute => !!r.meta?.navbar);

interface MenuItemLikeButtonProps {
  title: string;
  className?: string;
  onClick?: () => void;
  iconName?: string;
  disabled?: boolean;
  id: string;
  image?: boolean;
  onDisableClick?: () => void;
}
interface ReportIncidentButtonProps {
  title: string;
  className?: string;
  onClick?: () => void;
  iconName?: string | React.ElementType;
  disabled?: boolean;
  id: string;
  image?: boolean;
}

interface OwnProps {
  className?: string;
}
interface PropsFromState {
  authState: AuthState;
  language: { language: string };
}
interface PropsFromDispatch {
  setAuth: typeof setAuthAction;
  setCloseWalkThroughHomepage: typeof setInitialCloseWalkThroughAction;
}

/* NEW: router comes from WithRouter */
interface RouterPropsFromHOC {
  router: RouterProps;
}

/* Combine everything */
type ReduxProps = OwnProps & PropsFromState & PropsFromDispatch;
type Props = NewProps<ReduxProps & RouterPropsFromHOC, unknown>;

const mapStateToProps = (state: AppState) => ({
  authState: authStateSelector(state),
  language: languageSelector(state),
});

const mapDispatchToProps = (dispatch: AppDispatch): PropsFromDispatch => ({
  setAuth: (params) => dispatch(setAuthAction(params)),
  setCloseWalkThroughHomepage: (params) =>
    dispatch(setInitialCloseWalkThroughAction(params)),
});

const requestOptions: {
  [key: string]: ClientAttributes<ReduxProps, Record<string, never>>;
} = {
  logoutRequest: {
    url: "/auth/logout/",
    method: methods.POST,
    onSuccess: ({ props }) => {
      const authState = getAuthState();
      const { setAuth } = props;
      setAuth(authState);
      window.location.reload();
    },
    onFailure: ({ error }) => {
      console.warn("logout failure", error);
    },
    onFatal: () => {
      console.warn("logout fatal");
    },
  },
};

const GroupMenuListContainer: React.FC<{
  title: string;
  className?: string;
  id: string;
  handleActiveGroupButton: (active: boolean) => void;
  disabled?: boolean;
  image?: boolean;
  children: React.ReactNode;
  disableOutsideDivClick?: boolean;
}> = ({
  title,
  className,
  id,
  handleActiveGroupButton,
  children,
  disableOutsideDivClick,
}) => {
  const [showInfo1, setShowInfo1] = React.useState<boolean>(false);
  React.useEffect(() => {
    handleActiveGroupButton(showInfo1);
  }, [handleActiveGroupButton, showInfo1]);
  React.useEffect(() => {
    if (!disableOutsideDivClick) {
      setShowInfo1(false);
    }
  }, [disableOutsideDivClick]);

  return (
    <div className={styles.container}>
      <div className={styles.infoBoxWrapper}>
        <div
          role="presentation"
          className={_cs(styles.menuItemLikeButton, className)}
          onClick={() => setShowInfo1(true)}
          title={title}
          id={id}
        >
          <ButtonGroupLogo />
          <div className={styles.title}>{title}</div>
        </div>
        <GroupMenuContainer
          show={showInfo1}
          onClickOutside={() => {
            if (!disableOutsideDivClick) setShowInfo1(false);
          }}
        >
          {children}
        </GroupMenuContainer>
      </div>
    </div>
  );
};

const MenuItemLikeButton = ({
  title,
  className,
  onClick,
  iconName,
  disabled,
  id,
  image,
  onDisableClick,
}: MenuItemLikeButtonProps) => (
  <div
    role="presentation"
    className={_cs(styles.menuItemLikeButton, className)}
    onClick={
      !disabled
        ? () => {
            onClick?.();
            onDisableClick?.();
          }
        : undefined
    }
    title={title}
    id={id}
  >
    {image ? (
      <ScalableVectorGraphics className={styles.infoIconMax} src={iconName} />
    ) : (
      <Icon className={styles.icon} name={iconName} />
    )}
    <div className={styles.title}>{title}</div>
  </div>
);
const ReportIncidentButton = ({
  title,
  className,
  onClick,
  iconName,
  disabled,
  id,
  image,
}: ReportIncidentButtonProps) => (
  <div
    role="presentation"
    className={_cs(styles.menuItemLikeButton, className)}
    onClick={
      !disabled
        ? () => {
            onClick?.();
          }
        : undefined
    }
    title={title}
    id={id}
  >
    <div className={styles.reportIncidentTitle}>{title}</div>

    {image ? (
      <div className={styles.incidentButtonImagePart}>
        <ReportIncidentIcon className={styles.infoIconMax} />
      </div>
    ) : (
      <Icon className={styles.icon} name={iconName} />
    )}
  </div>
);
const ReportIncidentModalButton =
  modalize<ReportIncidentButtonProps>(ReportIncidentButton);
const ModalButton = modalize<MenuItemLikeButtonProps>(MenuItemLikeButton);

const menuKeySelector = (d: NavbarRoute) => d.meta.name;

const gotoadmin = (router: RouterProps | undefined) => {
  if (router) router.navigate("/admin");
};

class Navbar extends React.PureComponent<
  Props,
  { activeGroupButton: boolean; disableOutsideDivClick: boolean }
> {
  constructor(props: Props) {
    super(props);
    this.state = { activeGroupButton: false, disableOutsideDivClick: false };
  }

  private menuRendererParams = (
    _: string,
    data: NavbarRoute
  ): MenuItemProps => ({
    title:
      this.props.language.language === "en"
        ? data.meta.title
        : (data.meta.titleNep ?? data.meta.title),
    link: data.path, // ensure path exists
    disabled: data.meta.disabled ?? false,
    iconName: data.meta.iconName,
    className: styles.menuItem,
    id: data.meta.id ?? data.meta.title, // fallback to title if id missing
    onPreload: () => preloadRouteByName(data.meta.name),
  });

  private preloadRouteTimers: number[] = [];

  public componentDidMount() {
    const activeRouteName = this.context.activeRouteDetails?.name;
    this.preloadRouteTimers = NAVBAR_ROUTE_PRELOAD_ORDER.filter(
      (routeName) => routeName !== activeRouteName
    ).map((routeName, index) =>
      window.setTimeout(
        () => {
          preloadRouteByName(routeName);
        },
        1500 + index * 700
      )
    );
  }

  public componentWillUnmount() {
    this.preloadRouteTimers.forEach((timer) => {
      window.clearTimeout(timer);
    });
  }

  private handleActiveGroupButton = (data: boolean) => {
    this.setState({ activeGroupButton: data });
  };

  private handledisableOutsideDivClick = (bool: boolean) => {
    this.setState({ disableOutsideDivClick: bool });
  };

  public render() {
    const {
      className,
      authState,
      requests: { logoutRequest },
      router,
    } = this.props;
    const { activeRouteDetails } = this.context;
    const { activeGroupButton, disableOutsideDivClick } = this.state;
    const { authenticated, user } = authState;
    const activeRouteName = activeRouteDetails?.name;
    const GroupMenuListRoutes = ["droughtWatch", "DRRM Report", "ibf"];
    const isRoutedListedHere = GroupMenuListRoutes.includes(activeRouteName);

    const data = authenticated
      ? pagesNew
      : pagesNew.filter((i) => !(i.meta.disableIfNoAuth ?? false));

    return (
      <nav className={_cs(styles.navbar, className)}>
        <ListView
          data={data}
          keySelector={menuKeySelector}
          renderer={MenuItem}
          rendererParams={this.menuRendererParams}
          className={styles.menuItemList}
        />
        <div className={styles.bottom}>
          {activeRouteName === "incident" && (
            <Translation>
              {(t) => (
                <ReportIncidentModalButton
                  className={styles.reportIncident}
                  title={t("Report an incident")}
                  id="report-an-incident"
                  iconName={ReportIncidentIcon}
                  image
                  modal={<CitizenReportFormModal />}
                />
              )}
            </Translation>
          )}
          <GroupMenuListContainer
            className={
              activeGroupButton || isRoutedListedHere
                ? styles.logoutButtonActive
                : styles.buttomGroup
            }
            title=""
            id="group-menu-list"
            handleActiveGroupButton={this.handleActiveGroupButton}
            disableOutsideDivClick={disableOutsideDivClick}
          >
            {authenticated && (
              <Translation>
                {(t) => (
                  <ModalButton
                    className={_cs(
                      styles.reliefButton,
                      styles.insideContainerComponent
                    )}
                    title={t("Relief")}
                    iconName="cart"
                    id="relief"
                    modal={<Relief />}
                  />
                )}
              </Translation>
            )}
            {authenticated && (
              <Translation>
                {(t) => (
                  <ModalButton
                    className={_cs(
                      styles.reportIncidentButton,
                      styles.insideContainerComponent
                    )}
                    title={t("Reported incidents")}
                    id="reported-incidents"
                    iconName="list"
                    modal={<CitizenReportsModal />}
                  />
                )}
              </Translation>
            )}
            <Translation>
              {() => (
                <ModalButton
                  className={_cs(
                    styles.reportIncidentButton,
                    styles.insideContainerComponent
                  )}
                  title="Feedback and support"
                  id="feedback-and-support"
                  iconName="feedbackIcon"
                  modal={
                    <FeedbackSupport
                      handledisableOutsideDivClick={
                        this.handledisableOutsideDivClick
                      }
                    />
                  }
                  onDisableClick={() =>
                    this.setState({ disableOutsideDivClick: true })
                  }
                />
              )}
            </Translation>

            <Translation>
              {(t) => (
                <ModalButton
                  className={_cs(
                    styles.reportIncidentButton,
                    styles.insideContainerComponent
                  )}
                  title={t("About Us")}
                  id="about-us"
                  iconName="aboutUs"
                  modal={
                    <AboutModal
                      handledisableOutsideDivClick={
                        this.handledisableOutsideDivClick
                      }
                    />
                  }
                  onDisableClick={() =>
                    this.setState({ disableOutsideDivClick: true })
                  }
                />
              )}
            </Translation>
            <Translation>
              {(t) => (
                <ModalButton
                  className={_cs(
                    styles.reportIncidentButton,
                    styles.insideContainerComponent
                  )}
                  title={t("Situation Report")}
                  id="situation-report"
                  iconName="textDocument"
                  modal={
                    <SituationReport
                      handledisableOutsideDivClick={
                        this.handledisableOutsideDivClick
                      }
                    />
                  }
                  onDisableClick={() =>
                    this.setState({ disableOutsideDivClick: true })
                  }
                />
              )}
            </Translation>
            <Translation>
              {(t) => (
                <MenuItemLikeButton
                  className={
                    isRoutedListedHere && activeRouteName === "DRRM Report"
                      ? _cs(
                          styles.reportIncidentutton,
                          styles.insideContainerComponent
                        )
                      : styles.insideContainerComponent
                  }
                  title={t("DRRM Report")}
                  id="drrm"
                  iconName="textDocument"
                  onClick={() => router.navigate("/drrm-report/")}
                />
              )}
            </Translation>
            <Translation>
              {(t) => (
                <MenuItemLikeButton
                  className={styles.insideContainerComponent}
                  title={t("Satark")}
                  id="satark"
                  iconName="textDocument"
                  onClick={() =>
                    window.open("https://satark.bipadportal.gov.np/", "_blank")
                  }
                />
              )}
            </Translation>
          </GroupMenuListContainer>
          {!authenticated && (
            <Translation>
              {(t) => (
                <ModalButton
                  className={styles.menuItem}
                  title={t("Login")}
                  iconName="login"
                  id="login"
                  modal={<NewLoginModal />}
                />
              )}
            </Translation>
          )}

          {user && (
            <Translation>
              {(t) => (
                <MenuItemLikeButton
                  className={styles.logoutButton}
                  title={t("Admin")}
                  id="admin"
                  iconName="user"
                  onClick={() => gotoadmin(router)}
                  disabled={logoutRequest.pending}
                />
              )}
            </Translation>
          )}

          {authenticated && (
            <Translation>
              {(t) => (
                <MenuItemLikeButton
                  className={styles.logoutButton}
                  title={t("Logout")}
                  id="logout"
                  iconName="logout"
                  onClick={() => {
                    logoutRequest.do();
                    router.navigate("/");
                  }}
                  disabled={logoutRequest.pending}
                />
              )}
            </Translation>
          )}
        </div>
      </nav>
    );
  }
}
Navbar.contextType = PageContext;
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  createConnectedRequestCoordinator<ReduxProps>()(
    createRequestClient(requestOptions)(WithRouter(Navbar))
  )
);
